export interface Section {
  name: string;
  content: string;
}

// Default mapping of raw tags (lowercase) to UI display section names
const DEFAULT_TAG_CONFIG: Record<string, string> = {
  think: "Reasoning",
  processing: "Processing",
};

const DEFAULT_ANSWER_SECTION_NAME = "Answer";

/**
 * Parses a raw markdown string and divides it into sections based on custom tags.
 *
 * Tags are expected in the format <tagName>content</tagName>.
 * The tag names themselves are case-insensitive during matching but should be
 * provided in lowercase in the tagConfig.
 * Content not within any recognized tag is grouped into "Answer" sections.
 *
 * @param markdown The raw markdown string to parse.
 * @param tagConfig Optional: A record mapping lowercase tag names (e.g., "think")
 *                  to display section names (e.g., "Reasoning").
 *                  Defaults to `DEFAULT_TAG_CONFIG`.
 * @param defaultAnswerName Optional: The name for sections that are not part of any
 *                          recognized tag. Defaults to `DEFAULT_ANSWER_SECTION_NAME`.
 * @returns An array of Section objects. If the input markdown is empty or only whitespace,
 *          an empty array is returned.
 */
export function parseMarkdownToSections(
  markdown: string,
  tagConfig: Record<string, string> = DEFAULT_TAG_CONFIG,
  defaultAnswerName: string = DEFAULT_ANSWER_SECTION_NAME,
): Section[] {
  // Return early for null, undefined, or truly empty strings
  if (!markdown) {
    return [];
  }
  // If markdown is just whitespace, treat as empty for sectioning purposes.
  // The UI will likely render whitespace if it's the sole content of a section.
  if (markdown.trim() === "") {
    // If the original string was non-empty (i.e., whitespace),
    // and an "Answer" section with that whitespace is desired, this could be:
    // return [{ name: defaultAnswerName, content: markdown }];
    // For now, returning empty aligns with no meaningful sections.
    return [];
  }

  const sectionsInternal: Section[] = [];
  const tagNames = Object.keys(tagConfig);

  if (tagNames.length === 0) {
    // No tags configured, all content is a single default section
    sectionsInternal.push({ name: defaultAnswerName, content: markdown });
    return sectionsInternal;
  }

  // Regex to find any opening or closing tag specified in tagConfig
  // Group 1: Full tag (e.g., <think> or </think>)
  // Group 2: Tag name (e.g., think)
  const tagRegex = new RegExp(String.raw`(<\/?(${tagNames.join("|")})>)`, "gi");

  let lastIndex = 0;
  let currentSectionType = defaultAnswerName;
  let openRawTagName: string | null = null; // Stores the raw name of the currently open tag, e.g., "think"
  let currentContentBuffer = "";

  let match: RegExpExecArray | null;
  match = tagRegex.exec(markdown); // Initialize match
  while (match !== null) {
    const textBeforeTag = markdown.substring(lastIndex, match.index);
    currentContentBuffer += textBeforeTag;

    const fullTag = match[0]; // e.g., "<think>" or "</think>"
    const capturedTagName = match[2]; // Group 2: Tag name (e.g., think)

    // If the second capture group (the tag name) is undefined,
    // it's an unexpected state (e.g. empty tagNames array, or regex issue).
    // Treat the fullTag as literal content and skip tag processing.
    if (capturedTagName === undefined) {
      currentContentBuffer += fullTag;
      lastIndex = tagRegex.lastIndex; // Update lastIndex based on current match
      match = tagRegex.exec(markdown); // Get next match before continuing
      continue;
    }
    const rawTagName = capturedTagName.toLowerCase(); // e.g., "think"
    const isOpeningTag = !fullTag.startsWith("</");

    if (isOpeningTag) {
      // The current section (defined by `currentSectionType`) effectively ends here.
      // Push it if it has meaningful content or if it's an explicit section type.
      if (currentContentBuffer.trim() || currentSectionType !== defaultAnswerName) {
        sectionsInternal.push({ name: currentSectionType, content: currentContentBuffer.trim() });
      }
      currentContentBuffer = ""; // Reset buffer for the new section
      currentSectionType = tagConfig[rawTagName] || rawTagName; // Start new section type
      openRawTagName = rawTagName;
    } else {
      // Closing tag
      if (openRawTagName === rawTagName) {
        // Matched closing tag. The content accumulated in `currentContentBuffer` belongs to this section.
        // Finalize and push this section.
        if (
          currentContentBuffer.trim() ||
          (currentSectionType !== defaultAnswerName && currentSectionType === (tagConfig[rawTagName] || rawTagName))
        ) {
          sectionsInternal.push({ name: currentSectionType, content: currentContentBuffer.trim() });
        }
        currentContentBuffer = ""; // Reset buffer
        currentSectionType = defaultAnswerName; // Revert to default section type
        openRawTagName = null;
      } else {
        // Mismatched closing tag (e.g., </processing> when <think> was open, or </foo> when no tag was open).
        // Treat this tag as literal content for the current active section.
        currentContentBuffer += fullTag;
      }
    }
    // Update lastIndex based on current match
    lastIndex = tagRegex.lastIndex;
    // Get next match for the next iteration
    match = tagRegex.exec(markdown);
  }

  // Add any remaining text after the last processed tag
  currentContentBuffer += markdown.substring(lastIndex);

  // Push the final accumulated content.
  // This handles content after the last tag, or the entire string if no tags were found,
  // or an unclosed tagged section.
  if (currentContentBuffer.trim() || currentSectionType !== defaultAnswerName) {
    sectionsInternal.push({ name: currentSectionType, content: currentContentBuffer.trim() });
  }

  // Filter out any leading "Answer" section if it's empty and there are subsequent sections.
  // This can happen if the markdown starts directly with a known tag.
  if (sectionsInternal.length > 1) {
    const firstSection = sectionsInternal[0];
    if (firstSection && firstSection.name === defaultAnswerName && firstSection.content === "") {
      sectionsInternal.shift();
    }
  }

  // If after all processing, no sections were generated but the original markdown had non-whitespace content,
  // it implies the markdown consisted only of tags that resulted in empty sections, or some other edge case.
  // Ensure at least one section representing the original content if it was not just whitespace.
  if (sectionsInternal.length === 0 && markdown.trim().length > 0) {
    return [{ name: defaultAnswerName, content: markdown.trim() }];
  }

  // Final filter: remove "Answer" sections that are empty, but keep explicit sections if they are empty.
  return sectionsInternal.filter((section) => {
    if (section.name === defaultAnswerName) {
      return section.content !== ""; // Keep Answer sections only if they have non-empty (even if just whitespace) content after trim.
    }
    return true; // Keep all explicitly named sections (like Reasoning, Processing), even if their content is "".
  });
}
